import type { FlowState } from "./types";

export const FLOW_WINDOW_MS = 3000;
export const FLOW_ROUND_TRANSITION_WINDOW_MS = 3500;
export const MIN_VISIBLE_FLOW_COUNT = 2;

export function isFlowActive(flow: Pick<FlowState, "count">): boolean {
  return flow.count >= MIN_VISIBLE_FLOW_COUNT;
}

export function createInitialFlowState(): FlowState {
  return {
    count: 0,
    lastMoveAt: null,
    expiresAt: null,
    maxCount: 0,
    chainsStarted: 0,
    totalMoves: 0
  };
}

export function resetActiveFlow(flow: FlowState): FlowState {
  if (flow.count === 0 && flow.lastMoveAt === null && flow.expiresAt === null) {
    return flow;
  }

  return {
    ...flow,
    count: 0,
    lastMoveAt: null,
    expiresAt: null
  };
}

export function registerFlowMove(flow: FlowState, playedAt: number): FlowState {
  const nextCount = getNextFlowCount(flow, playedAt);
  const withinWindow = isFlowActive({ count: nextCount });

  return {
    count: nextCount,
    lastMoveAt: playedAt,
    expiresAt: playedAt + FLOW_WINDOW_MS,
    maxCount: Math.max(flow.maxCount, nextCount),
    chainsStarted: withinWindow ? flow.chainsStarted : flow.chainsStarted + 1,
    totalMoves: flow.totalMoves + 1
  };
}

export function extendFlowAcrossRound(flow: FlowState): FlowState {
  if (flow.count === 0 || flow.lastMoveAt === null) {
    return flow;
  }

  return {
    ...flow,
    expiresAt: flow.lastMoveAt + FLOW_ROUND_TRANSITION_WINDOW_MS
  };
}

export function getNextFlowCount(flow: FlowState, playedAt: number): number {
  const withinWindow =
    flow.lastMoveAt !== null && playedAt - flow.lastMoveAt <= FLOW_WINDOW_MS;

  return withinWindow ? flow.count + 1 : 1;
}

export function expireFlow(flow: FlowState, expiresAt: number): FlowState {
  if (flow.expiresAt !== expiresAt) {
    return flow;
  }

  return resetActiveFlow(flow);
}

export function getAverageFlowChainLength(flow: FlowState): number {
  if (flow.chainsStarted === 0) {
    return 0;
  }

  return Math.round((flow.totalMoves / flow.chainsStarted) * 100) / 100;
}
